import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { DraftGroup, IMatchupsResponse, IOptimizerState, IParlayOutcome, IShopOffer, IShopOutcome, IShopOutcomesPagination, IState, IToolFilters, Leagues, Operators, PickemParlayType, shopOddStates } from '../../pick6/store/types';
import { IAdditionalParlayLegsFilters, IOffers, IParlayFilters, IToolsState, underDogDescription } from './types';
import { APIState } from '../../utils/api/reducer';
import { APIConstants } from '../../utils/api/constants';

const initialState = {
  operator: 'PrizePicks',
  league: Leagues.ALL,
  pickemParlayType: 'Flex',
  parlaysData: {
    status: APIConstants.SUCCEEDED,
    data: [],
  } as APIState<IParlayOutcome[]>,
  matchupsByLeague: {} as APIState<IMatchupsResponse[]>,
  wager:10,
  arbitrageWager: '',
  timeZone: '',
  projectionSystem: 'sharp',
  sportsbookCodes: [],
  userSelectedSportsbookCodes: [],
  additionalLegs: {} as APIState<{
    outcomes: IShopOutcome[],
    pagination: Partial<IShopOutcomesPagination>,
  }>,
  oddsBoostAdditionalLegs: {} as APIState<{
    outcomes: IShopOutcome[],
    pagination: Partial<IShopOutcomesPagination>,
  }>,
  parlayFilters: {
    projectionSystem: 'sharp',
    league: Leagues.ALL,
    timeframe: 'future7',
    operator: 'PrizePicks',
  } as IParlayFilters,
  additionalParlayLegFilters: {
    league: Leagues.ALL,
  } as IAdditionalParlayLegsFilters,
  selectedLegs: [] as IParlayOutcome[],
  oddsBoostSelectedLegs: [] as IShopOutcome[],
  state: {
    id: '115b1b1e-3875-4d6a-a842-b29456e821de',
    name: 'Illinois',
    abbreviation: 'IL',
    isLegalized: true,
  } as IState,
  arbitrageLoading: false,
  maxHold: 0,
  filters: {
    state: {
      id: '115b1b1e-3875-4d6a-a842-b29456e821de',
      name: 'Illinois',
      abbreviation: 'IL',
      isLegalized: true,
    } as IState,
  } as IToolFilters,
  optimizerState: {
    matchups: [],
    matchupsFilter: [],
    offersFilter: [],
    entries: [],
    gridpaginationModel : {
      page: 0,
      pageSize: 25
    },
    selectedEntry: 0,
    additionalLegs: {} as any,
    isEntryView: false,
    optimizerData: [],
    selectedDraftGroup:{} as DraftGroup,
  } as Partial<IOptimizerState>,
  sgpState: {
    selectedBets: [],
    sgpPage: 'matchup-select',
  },
  timeframe: 'future7',
  sportsbook: 'BetMGM',
  offers: {},
  shopOddsStates: shopOddStates,
  description: `<h2>PrizePicks Entry Builder Explained</h2> <h3>How do I place a PrizePicks entry?</h3> <ol> <li>Choose 2 or more players from the PrizePicks board using Stokastic's Best Picks we've listed here or add your own picks.</li> <li>Pick each player to have MORE or LESS than their projected fantasy score or single stat.</li> <li>Choose Flex Play or Power Play.</li> <li>With Flex Play, you can miss one and still win!</li> <li>HINT: Flex Play is the safer option.</li> <li>With Power Play, you must get all your picks correct to win.</li> </ol> <p>The word has been out on <a href="https://bit.ly/PrizePicksStokastic17" rel="nofollow" target="_new">PrizePicks</a> for a while. The unique betting platform is different from traditional sportsbooks in that they offer some new and exciting ways to get action on player props.</p> <p>DFS players and sports bettors have flocked to <strong>PrizePicks</strong> as an alternative way to get action on sports. Even as more and more states legalize sports betting, sites like <strong>PrizePicks</strong> provide a unique and profitable user experience with plenty of opportunities to find an edge.</p> <p>The +EV betting spots on <a href="https://bit.ly/PrizePicksStokastic17" rel="nofollow" target="_new">PrizePicks</a> are expanding, and getting pretty exciting, with their introduction of a new feature to get 25x on your wager for a six-pick entry called a “flex play.” While those kinds of payouts can make you wonder if PrizePicks is legit, it most certainly is!</p> <p>If you want to learn more about <strong>PrizePicks</strong>, or Flex Picks, or even how to find value, we have you covered.</p>`
  } as IToolsState;

const pickemOptimizerSlice = createSlice({
  name: 'pickemOptimizer',
  initialState,
  reducers: {
    setOffers: (state, action: PayloadAction<IOffers>) => {
      state.offers = action.payload;
    },
    setOperator: (state, action: PayloadAction<Operators>) => {
      state.operator = action.payload;
      state.parlayFilters.operator = action.payload;
      if(action.payload === Operators.UNDERDOG) {
        state.description = underDogDescription;
      }
    },
    removeLeg: (state, action: PayloadAction<IParlayOutcome>) => {
      state.selectedLegs = state.selectedLegs.filter(
        (l) => l.hashCode !== action.payload.hashCode,
      );
    },
    setParlaysData: (state, action: PayloadAction<APIState<IParlayOutcome[]>>) => {
      state.parlaysData = {
        status: action?.payload?.status,
        data: action.payload?.data || [],
      };
    },
    resetLegs: (state, action: PayloadAction<IParlayOutcome[]>) => {
      state.selectedLegs = action.payload;
    },
    addLeg: (state, action: PayloadAction<IParlayOutcome>) => {
      state.selectedLegs = state.selectedLegs.concat(action.payload);
    },
    setAdditionalLegs: (state, action: PayloadAction<{
      status: APIConstants,
      data: {
        outcomes: IShopOutcome[],
        pagination: Partial<IShopOutcomesPagination>,
      }
    }>) => {
      state.additionalLegs = {
        status: action?.payload?.status,
        data: {outcomes: action.payload.data.outcomes || [], pagination: action.payload.data.pagination || {}},
      };
    },
    swapLegs: (state, action: PayloadAction<IParlayOutcome>) => {
      state.selectedLegs = state.selectedLegs
        .filter(
          (s) => `${s.eventId?.toLowerCase()}|${s.homeTeamId?.toLowerCase()}|${s.awayTeamId?.toLowerCase()}` !== `${action.payload.eventId?.toLowerCase()}|${action.payload.homeTeamId?.toLowerCase()}|${action.payload.awayTeamId?.toLowerCase()}`,
        )
        .concat(action.payload);;
    },
    parlayBuilderFiltersMinOdds: (state, action: PayloadAction<string>) => {
      state.parlayFilters = {
        ...state.parlayFilters,
        minOdds: action.payload
      }
    },
    setPickemParlayType: (state, action: PayloadAction<PickemParlayType>) => {
      state.parlayFilters = {
        ...state.parlayFilters,
        pickemParlayType: action.payload
      }
    },
    setTimeFrame: (state, action: PayloadAction<string>) => {
      state.parlayFilters = {
        ...state.parlayFilters,
        timeframe: action.payload
      }
      state.timeframe = action.payload;
    },
    setState: (state, action: PayloadAction<IState>) => {
      state.filters = {
        ...state.filters,
        state: action.payload
      }
      state.state = action.payload
    },
    setAdditionalFiltersLeague: (state, action: PayloadAction<Leagues>) => {
      state.additionalParlayLegFilters = {
        ...state.additionalParlayLegFilters,
        league: action.payload,
        events: undefined,
      };
      state.league = action.payload;
    },
    addMatchupFilter: (state, action: PayloadAction<IMatchupsResponse>) => {
      state.additionalParlayLegFilters = {
        ...state.additionalParlayLegFilters,
        events: state.additionalParlayLegFilters.events
          ? state.additionalParlayLegFilters.events.concat(action.payload.id)
          : [action.payload.id],
      }
    },
    removeMatchupFilter: (state, action: PayloadAction<IMatchupsResponse>) => {
      state.additionalParlayLegFilters = {
        ...state.additionalParlayLegFilters,
        events:
          state.additionalParlayLegFilters.events && state.additionalParlayLegFilters.events.length > 1
            ? [...state.additionalParlayLegFilters.events.filter((ma) => ma !== action.payload.id)]
            : undefined,
      }
    },
    addBetTypeFilters: (state, action: PayloadAction<IShopOffer>) => {
      state.additionalParlayLegFilters = {
        ...state.additionalParlayLegFilters,
        betTypes: state.additionalParlayLegFilters.betTypes
          ? state.additionalParlayLegFilters.betTypes.concat(action.payload)
          : [action.payload],
      }
    },
    removeBetTypeFilters: (state, action: PayloadAction<IShopOffer>) => {
      state.additionalParlayLegFilters = {
        ...state.additionalParlayLegFilters,
        betTypes:
          state.additionalParlayLegFilters.betTypes && state.additionalParlayLegFilters.betTypes.length > 1
            ? [...state.additionalParlayLegFilters.betTypes.filter((ma) => ma.id !== action.payload.id)]
            : undefined,
      }
    },
    setMatchups: (state, action: PayloadAction<APIState<IMatchupsResponse[]>>) => {
      state.matchupsByLeague = {
        status: action.payload.status,
        data: action.payload.data || [],
      };
    },
  },
});

export const { 
  removeLeg,
  resetLegs,
  addLeg,
  parlayBuilderFiltersMinOdds,
  setPickemParlayType,
  setTimeFrame,
  setState,
  setAdditionalFiltersLeague,
  swapLegs,
  addMatchupFilter,
  removeMatchupFilter,
  addBetTypeFilters,
  removeBetTypeFilters,
  setParlaysData,
  setAdditionalLegs,
  setMatchups,
  setOffers,
  setOperator,
 } = pickemOptimizerSlice.actions;

export default pickemOptimizerSlice.reducer;